import { FC, useEffect, useRef, useState } from 'react';

import { Button, message } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';

import { PhoneIdentity } from '@/types/types';
import { phoneNameSpace } from '@/components/Common/PhoneComp';
import styles from './MissingDetails.module.scss';

interface MissingPhoneInputProps {
    phone?: string;
    prefix?: string;
    onSubmit: (value: PhoneIdentity) => void;
    onEditingChange?: (isEditing: boolean) => void;
    initialEditing?: boolean;
    alwaysShowInput?: boolean;
}

const MissingPhoneInput: FC<MissingPhoneInputProps> = ({
    onSubmit,
    phone,
    prefix,
    onEditingChange,
    initialEditing = false,
    alwaysShowInput = false,
}) => {
    const hasExistingPhone = !!(phone && prefix);
    const [phoneNumberValue, setPhoneNumberValue] = useState<string | undefined>('');
    const [submittedPhone, setSubmittedPhone] = useState<PhoneIdentity>(null);
    const [isEditing, setIsEditing] = useState(initialEditing);
    const hasSyncedRef = useRef(false);
    const skipOnChangeRef = useRef(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (alwaysShowInput && phone && prefix && !hasSyncedRef.current) {
            skipOnChangeRef.current = true;
            setPhoneNumberValue(`+${prefix}${phone}`);
            hasSyncedRef.current = true;
        }
    }, [alwaysShowInput, phone, prefix]);

    const setEditingState = (value: boolean): void => {
        setIsEditing(value);
        onEditingChange?.(value);
    };

    const displayPhone = submittedPhone || (hasExistingPhone ? { phone, prefix } : null);

    if (displayPhone && !isEditing && !alwaysShowInput) {
        return (
            <div
                className={styles['phone-chip']}
                onClick={() => setEditingState(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingState(true); }}
            >
                +{displayPhone.prefix} {displayPhone.phone}
            </div>
        );
    }

    const submitPhone = (): void => {
        if (isValidPhoneNumber(phoneNumberValue)) {
            const phoneNumber = parsePhoneNumber(phoneNumberValue);
            const callingCode = getCountryCallingCode(phoneNumber.country);
            const newPhone = { phone: phoneNumber.nationalNumber, prefix: callingCode };
            onSubmit(newPhone);
            setSubmittedPhone(newPhone);
            setEditingState(false);
        } else {
            message.error(t(`${phoneNameSpace}.invalidPhone`));
        }
    };

    const handleSubmit = (e: React.SyntheticEvent): void => {
        e.preventDefault();
        submitPhone();
    };

    const handleKeyUp = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            submitPhone();
        }
    };

    const handleClear = (e: React.SyntheticEvent): void => {
        e.preventDefault();
        if (displayPhone) {
            setPhoneNumberValue('');
            setEditingState(false);
        } else {
            setPhoneNumberValue('');
        }
    };

    return (
        <div className={styles['phone-input-row']}>
            <PhoneInput
                className={styles['phone-input']}
                value={phoneNumberValue}
                onChange={(value) => {
                    if (skipOnChangeRef.current) {
                        skipOnChangeRef.current = false;
                        return;
                    }
                    setPhoneNumberValue(value);
                }}
                onKeyUp={handleKeyUp}
                defaultCountry="IL"
                international
                smartCaret
                flags={flags}
                limitMaxLength
            />
            <div className={styles['action-buttons']}>
                <Button
                    type="link"
                    className={styles['submit-btn']}
                    onClick={handleSubmit}
                    disabled={!phoneNumberValue || !isValidPhoneNumber(phoneNumberValue)}
                >
                    <CheckOutlined />
                </Button>
                <Button
                    type="link"
                    className={styles['cancel-btn']}
                    onClick={handleClear}
                    disabled={!phoneNumberValue && !displayPhone}
                >
                    <CloseOutlined />
                </Button>
            </div>
        </div>
    );
};

export default MissingPhoneInput;
